// Data + resolver for the theme page. Token lists mirror the design board's
// foundations; `useResolvedTokens` reads their live computed values so swatches
// show real values and update when the light/dark theme flips.

import { useEffect, useState } from 'react';

/* ---- Tier 1 · primitive ramps (documented; product UI must not use directly) ---- */
export interface Ramp {
  title: string;
  steps: string[];
}

export const PRIMITIVE_RAMPS: Ramp[] = [
  {
    title: 'Brand green (primary = --g-600)',
    steps: [
      '--g-50',
      '--g-100',
      '--g-200',
      '--g-300',
      '--g-400',
      '--g-500',
      '--g-600',
      '--g-700',
      '--g-800',
      '--g-900',
    ],
  },
  {
    title: 'Neutral slate',
    steps: [
      '--n-0',
      '--n-25',
      '--n-50',
      '--n-100',
      '--n-200',
      '--n-300',
      '--n-400',
      '--n-500',
      '--n-600',
      '--n-700',
      '--n-800',
      '--n-900',
      '--n-950',
    ],
  },
  {
    title: 'Blue · info',
    steps: ['--blue-50', '--blue-100', '--blue-500', '--blue-600', '--blue-700'],
  },
  {
    title: 'Amber · warning',
    steps: ['--amber-50', '--amber-100', '--amber-500', '--amber-600', '--amber-700'],
  },
  {
    title: 'Red · danger',
    steps: ['--red-50', '--red-100', '--red-500', '--red-600', '--red-700'],
  },
  {
    title: 'Violet · secondary',
    steps: ['--violet-50', '--violet-100', '--violet-500', '--violet-600'],
  },
  { title: 'Accent · warm highlight', steps: ['--accent-400', '--accent-500', '--accent-600'] },
];

/* ---- Tier 2 · semantic roles (what product UI references) ---- */
export interface TokenGroup {
  title: string;
  tokens: string[];
}

export const SEMANTIC_GROUPS: TokenGroup[] = [
  {
    title: 'Brand & CTA',
    tokens: [
      '--brand',
      '--brand-hover',
      '--brand-active',
      '--brand-fg',
      '--brand-subtle',
      '--brand-subtle-fg',
      '--brand-border',
    ],
  },
  {
    title: 'Surface & background',
    tokens: ['--bg', '--surface', '--surface-2', '--surface-sunken', '--surface-hover'],
  },
  { title: 'Text', tokens: ['--text', '--text-2', '--text-3', '--text-disabled', '--on-color'] },
  { title: 'Border', tokens: ['--border', '--border-strong', '--border-subtle'] },
  { title: 'Icon', tokens: ['--icon', '--icon-2', '--icon-disabled'] },
  { title: 'Accent', tokens: ['--accent', '--accent-subtle', '--accent-fg'] },
];

/* ---- Status intents (base / bg / fg / border) ---- */
export interface StatusToken {
  name: string;
  base?: string;
  bg: string;
  fg: string;
  border?: string;
}

export const STATUS_TOKENS: StatusToken[] = [
  {
    name: 'Success',
    base: '--success',
    bg: '--success-bg',
    fg: '--success-fg',
    border: '--success-border',
  },
  { name: 'Info', base: '--info', bg: '--info-bg', fg: '--info-fg', border: '--info-border' },
  {
    name: 'Warning',
    base: '--warning',
    bg: '--warning-bg',
    fg: '--warning-fg',
    border: '--warning-border',
  },
  {
    name: 'Danger',
    base: '--danger',
    bg: '--danger-bg',
    fg: '--danger-fg',
    border: '--danger-border',
  },
  { name: 'Purple', base: '--purple', bg: '--purple-bg', fg: '--purple-fg' },
  { name: 'Neutral', bg: '--neutral-bg', fg: '--neutral-fg' },
];

/* ---- Radius + elevation (radius values read live; shadows shown visually) ---- */
export const RADIUS_TOKENS = [
  '--radius-xs',
  '--radius-sm',
  '--radius-md',
  '--radius-lg',
  '--radius-xl',
  '--radius-full',
];
export const SHADOW_TOKENS = ['--sh-xs', '--sh-sm', '--sh-md', '--sh-lg', '--sh-xl', '--sh-brand'];

/* ---- Typography specimens — driven by the --fs-* type scale (theme.scss) ---- */
export interface TypeSpecimen {
  name: string;
  token: string;
  family: string;
}

export const TYPE_SPECIMENS: TypeSpecimen[] = [
  { name: 'Display', token: '--fs-display', family: "'PlusJakartaSans Extra Bold'" },
  { name: 'Heading 1', token: '--fs-h1', family: "'PlusJakartaSans Bold'" },
  { name: 'Heading 2', token: '--fs-h2', family: "'PlusJakartaSans Bold'" },
  { name: 'Heading 3', token: '--fs-h3', family: "'PlusJakartaSans Medium'" },
  { name: 'Heading 4', token: '--fs-h4', family: "'PlusJakartaSans Medium'" },
  { name: 'Body large', token: '--fs-body-lg', family: "'PlusJakartaSans Regular'" },
  { name: 'Body medium', token: '--fs-body-md', family: "'PlusJakartaSans Regular'" },
  { name: 'Body', token: '--fs-body', family: "'PlusJakartaSans Regular'" },
  { name: 'Caption', token: '--fs-caption', family: "'PlusJakartaSans Regular'" },
  { name: 'Sub-caption', token: '--fs-sub-caption', family: "'PlusJakartaSans Regular'" },
  { name: 'Micro / overline', token: '--fs-micro', family: "'PlusJakartaSans Medium'" },
];

/* ---- Spacing scale — the --space-* 4/8 grid (theme.scss) ---- */
export const SPACING_TOKENS = [
  '--space-1',
  '--space-2',
  '--space-3',
  '--space-4',
  '--space-5',
  '--space-6',
  '--space-7',
  '--space-8',
  '--space-9',
  '--space-10',
  '--space-11',
];

/* ---- Font families available (from fonts.scss) ---- */
export const FONT_FAMILIES = [
  { label: 'Regular', family: "'PlusJakartaSans Regular'" },
  { label: 'Medium', family: "'PlusJakartaSans Medium'" },
  { label: 'Semi Bold', family: "'PlusJakartaSans Semi Bold'" },
  { label: 'Bold', family: "'PlusJakartaSans Bold'" },
  { label: 'Extra Bold', family: "'PlusJakartaSans Extra Bold'" },
];

/* ---- Icon sampler — the names used across the app ---- */
export const ICON_SAMPLER = [
  'plus',
  'send',
  'trash-2',
  'pencil',
  'sliders-horizontal',
  'ellipsis',
  'ellipsis-vertical',
  'x',
  'check',
  'circle-alert',
  'chevron-down',
  'search',
  'map-pin',
  'mail',
  'lock',
  'users',
  'bell',
  'folder-kanban',
  'clipboard-check',
  'clipboard-list',
  'workflow',
  'shield-check',
  'network',
  'palette',
];

/**
 * Resolve the live computed value of each CSS custom property. Re-runs whenever
 * `document.documentElement[data-theme]` changes (the app's light/dark switch),
 * regardless of effect ordering, via a MutationObserver.
 */
export function useResolvedTokens(names: string[]): Record<string, string> {
  const key = names.join(',');
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const resolve = () => {
      const cs = getComputedStyle(document.documentElement);
      const next: Record<string, string> = {};
      for (const name of names) {
        let raw = cs.getPropertyValue(name).trim();
        // Semantic tokens defined as var(--primitive) may not fully substitute
        // in getPropertyValue — resolve those (colors) through a probe element.
        if (!raw || raw.includes('var(')) {
          const probe = document.createElement('span');
          probe.style.cssText = `position:absolute;visibility:hidden;color:var(${name})`;
          document.body.appendChild(probe);
          const resolved = getComputedStyle(probe).color;
          probe.remove();
          if (resolved) raw = resolved;
        }
        next[name] = raw;
      }
      setValues(next);
    };

    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
    // key captures the (stable) token list; names arrays are module-scoped constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return values;
}
